import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Render a long list progressively: the first `initial` items at once, then
 * `step` more whenever the end of what is rendered comes near the viewport.
 * Nothing changes visually — rows appear before they scroll into view — but
 * opening a 1500-member list no longer builds 1500 rows in one frame (that
 * froze the page for over a second, and six seconds in settings).
 *
 * `resetKey` starts over (a new search, another guild). Put `sentinelRef` on
 * an empty element after the rendered items while `limit < total`.
 */
export function useProgressiveList(
  total: number,
  opts: { initial?: number; step?: number; resetKey?: unknown } = {},
): { limit: number; sentinelRef: (node: Element | null) => void } {
  const initial = opts.initial ?? 120;
  const step = opts.step ?? 200;
  const [limit, setLimit] = useState(initial);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    setLimit(initial);
  }, [opts.resetKey, initial]);

  const sentinelRef = useCallback(
    (node: Element | null) => {
      observer.current?.disconnect();
      observer.current = null;
      if (!node || typeof IntersectionObserver === 'undefined') return;
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) setLimit((current) => current + step);
        },
        // Well before it is visible: the next rows are ready when scrolled to.
        { rootMargin: '800px 0px' },
      );
      observer.current.observe(node);
    },
    [step],
  );

  useEffect(() => () => observer.current?.disconnect(), []);

  return { limit: Math.min(limit, total), sentinelRef };
}
